import { useMemo, useState } from 'react'
import type { KeyboardEvent, MouseEvent, ReactNode } from 'react'
import { Button } from '../Button'
import { cn } from '../cn'
import { EmptyState } from '../EmptyState'
import { IconChevronDown, IconChevronExpand, IconChevronRight, IconChevronUp } from '../Icons'
import { SkeletonRows } from '../Skeleton'
import { BulkSelectBar } from './BulkSelectBar'
import { RowActions } from './RowActions'
import type { CollectionColumnDef, UseCollectionResult } from './useCollection'
import type { RowAction } from './RowActions'

export interface DataTableV2Props<RowT> {
  collection: UseCollectionResult<RowT>
  caption?: string
  selectable?: boolean
  /**
   * Accessible name for the header select-all checkbox. Override per surface
   * so assistive tech hears WHAT is being selected (e.g. "Select all inbox
   * leads") instead of the generic default.
   */
  selectAllLabel?: string
  /**
   * Accessible name for a row's select checkbox. Return a contextual name
   * (e.g. "Select Ada Lovelace") so assistive tech can tell rows apart;
   * defaults to the positional "Select row N".
   */
  getRowSelectLabel?: (row: RowT, rowIndex: number) => string
  rowActions?: RowAction<RowT>[]
  onRowClick?: (row: RowT) => void
  virtualized?: boolean
  rowHeight?: number
  maxBodyHeight?: number
  /** Error copy override; collection.uiState === 'error' still drives rendering. */
  errorMessage?: ReactNode
  /** Renders a secondary "Try again" in the error posture. */
  onRetry?: () => void
  retryLabel?: ReactNode
  /** Canon empty pattern slots (crm-tasks pattern: icon + title + expectation copy + action). */
  emptyIcon?: ReactNode
  emptyAction?: ReactNode
  emptyTitle?: string
  emptyMessage?: string
  className?: string
  /**
   * Classes applied to the inner `<table>` (e.g. a `min-w-[…]`), so wide
   * tables scroll horizontally IN PLACE inside the built-in overflow-x-auto
   * wrapper instead of blowing out the outer card (2026-07-08 TCP review).
   */
  tableClassName?: string
}

export function DataTableV2<RowT>({
  collection,
  caption,
  selectable = false,
  selectAllLabel = 'Select all visible rows',
  getRowSelectLabel,
  rowActions = [],
  onRowClick,
  virtualized = false,
  rowHeight = 44,
  maxBodyHeight = 440,
  errorMessage,
  onRetry,
  retryLabel,
  emptyIcon,
  emptyAction,
  emptyTitle = 'No results',
  emptyMessage = 'No rows match this collection.',
  className = '',
  tableClassName,
}: DataTableV2Props<RowT>) {
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set())
  const [scrollTop, setScrollTop] = useState(0)
  const hiddenColumns = collection.columns.filter((column) => (column.priority ?? 1) > 1)
  const useWindow = virtualized && collection.rows.length > 100
  const windowRows = useMemo(() => {
    if (!useWindow) return { start: 0, rows: collection.rows, top: 0, bottom: 0 }
    const overscan = 6
    const visible = Math.ceil(maxBodyHeight / rowHeight)
    const start = Math.max(0, Math.floor(scrollTop / rowHeight) - overscan)
    const end = Math.min(collection.rows.length, start + visible + overscan * 2)
    return { start, rows: collection.rows.slice(start, end), top: start * rowHeight, bottom: Math.max(0, (collection.rows.length - end) * rowHeight) }
  }, [collection.rows, maxBodyHeight, rowHeight, scrollTop, useWindow])
  const sortStatus = getSortStatus(collection)
  const collectionStateAttributes = {
    'data-collection-page': collection.state.page,
    'data-collection-page-size': collection.state.pageSize,
    'data-collection-total': collection.total,
    'data-collection-rendered-rows': collection.uiState === 'ready' ? windowRows.rows.length : 0,
    'data-collection-query-active': Boolean(collection.state.q.trim()),
    'data-collection-filter-count': collection.activeFilters.length,
  }

  if (collection.uiState === 'loading') {
    return <div data-collection data-collection-state="loading" {...collectionStateAttributes} className={cn('rounded-surface border border-line bg-white shadow-card', className)}><SkeletonRows rows={6} columns={collection.columns.length || 4} /></div>
  }
  if (collection.uiState === 'error') {
    return (
      <div data-collection data-collection-state="error" {...collectionStateAttributes} role="alert" className={cn('rounded-surface border border-line bg-white px-5 py-12 text-center shadow-card', className)}>
        <p className="font-display text-h4 text-strong m-0 mb-2">Couldn't load this data</p>
        <p className="m-0 mb-4 max-w-sm mx-auto text-ink-muted">{errorMessage ?? 'Could not load this collection.'}</p>
        {onRetry && <Button variant="secondary" onClick={onRetry}>{retryLabel ?? 'Try again'}</Button>}
      </div>
    )
  }
  if (collection.uiState === 'empty') {
    // Keep thead on empty boards so harvesters can still join column labels
    // (screen-truth Layer-3 tables are named `columns.join(" | ")`).
    const emptyColSpan = span(collection, selectable, hiddenColumns, rowActions)
    return (
      <div
        data-collection
        data-collection-state="empty"
        {...collectionStateAttributes}
        className={cn('min-w-0 overflow-hidden rounded-surface border border-line bg-white shadow-card', className)}
      >
        <div className="relative min-w-0 overflow-x-auto overscroll-x-contain shadow-scroll-hint">
          <table className={cn('w-full min-w-0 border-collapse text-sm', tableClassName)}>
            {caption && <caption className="sr-only">{caption}</caption>}
            <thead className="sticky top-0 z-sticky bg-white">
              <tr className="border-b border-line-strong">
                {selectable && (
                  <th scope="col" className="w-11 px-3 text-left">
                    <span className="sr-only">{selectAllLabel}</span>
                  </th>
                )}
                {hiddenColumns.length > 0 && (
                  <th scope="col" className="w-11 px-3 md:hidden">
                    <span className="sr-only">Details</span>
                  </th>
                )}
                {collection.columns.map((column) => (
                  <HeaderCell key={column.id} column={column} collection={collection} />
                ))}
                {rowActions.length > 0 && (
                  <th scope="col" className="w-11 px-3">
                    <span className="sr-only">Actions</span>
                  </th>
                )}
              </tr>
            </thead>
            <tbody>
              <tr>
                <td colSpan={emptyColSpan} className="px-5 py-12">
                  <EmptyState icon={emptyIcon} title={emptyTitle} action={emptyAction}>
                    {emptyMessage}
                  </EmptyState>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  return (
    <div data-collection data-collection-state="ready" {...collectionStateAttributes} aria-busy={collection.isRefreshing || undefined} className={cn('min-w-0 overflow-hidden rounded-surface border border-line bg-white shadow-card', className)}>
      <div role="status" aria-live="polite" className="sr-only">{sortStatus}</div>
      {selectable && <BulkSelectBar collection={collection} />}
      <div className="relative min-w-0 overflow-x-auto overscroll-x-contain shadow-scroll-hint" style={useWindow ? { maxHeight: maxBodyHeight, overflowY: 'auto' } : undefined} onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}>
        <table aria-rowcount={useWindow ? collection.rows.length : undefined} className={cn('w-full min-w-0 border-collapse text-sm', collection.isRefreshing && 'opacity-60', tableClassName)}>
          {caption && <caption className="sr-only">{caption}</caption>}
          <thead className="sticky top-0 z-sticky bg-white">
            <tr className="border-b border-line-strong">
              {selectable && (
                <th scope="col" className="w-11 px-3 text-left">
                  <input
                    type="checkbox"
                    aria-label={selectAllLabel}
                    aria-checked={collection.selection.someVisibleSelected && !collection.selection.allVisibleSelected ? 'mixed' : collection.selection.allVisibleSelected}
                    checked={collection.selection.allVisibleSelected}
                    onChange={collection.selection.toggleAllVisible}
                    className="size-4 rounded border-line focus-visible:outline-none focus-visible:shadow-focus"
                  />
                </th>
              )}
              {hiddenColumns.length > 0 && <th scope="col" className="w-11 px-3 md:hidden"><span className="sr-only">Details</span></th>}
              {collection.columns.map((column) => <HeaderCell key={column.id} column={column} collection={collection} />)}
              {rowActions.length > 0 && <th scope="col" className="w-11 px-3"><span className="sr-only">Actions</span></th>}
            </tr>
          </thead>
          <tbody>
            {useWindow && windowRows.top > 0 && <tr aria-hidden="true"><td colSpan={span(collection, selectable, hiddenColumns, rowActions)} style={{ height: windowRows.top }} /></tr>}
            {windowRows.rows.map((row, offset) => {
              const index = windowRows.start + offset
              const key = collection.getRowKey(row, index)
              return (
                <Row
                  key={key}
                  row={row}
                  rowIndex={index}
                  collection={collection}
                  selectable={selectable}
                  rowActions={rowActions}
                  onRowClick={onRowClick}
                  rowHeight={rowHeight}
                  expanded={expanded.has(key)}
                  onToggleExpanded={() => setExpanded((current) => toggleSet(current, key))}
                  hiddenColumns={hiddenColumns}
                  getRowSelectLabel={getRowSelectLabel}
                  windowed={useWindow}
                />
              )
            })}
            {useWindow && windowRows.bottom > 0 && <tr aria-hidden="true"><td colSpan={span(collection, selectable, hiddenColumns, rowActions)} style={{ height: windowRows.bottom }} /></tr>}
          </tbody>
        </table>
        {collection.isRefreshing && <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-white/20" />}
      </div>
    </div>
  )
}

function HeaderCell<RowT>({ column, collection }: { column: CollectionColumnDef<RowT>; collection: UseCollectionResult<RowT> }) {
  const stack = collection.state.sortStack?.length ? collection.state.sortStack : (collection.state.sort ? [collection.state.sort] : [])
  const rankIndex = stack.findIndex((sort) => sort.id === column.id)
  const sorted = rankIndex >= 0 ? stack[rankIndex].dir : null
  const sortable = column.sortable !== false
  const ariaSort = sorted ? (rankIndex === 0 ? (sorted === 'asc' ? 'ascending' : 'descending') : 'other') : sortable ? 'none' : undefined

  function toggle(event: MouseEvent<HTMLButtonElement>) {
    collection.toggleSort(column.id, { append: event.shiftKey })
  }

  function handleHeaderKey(event: KeyboardEvent<HTMLButtonElement>) {
    if (!event.shiftKey || (event.key !== 'Enter' && event.key !== ' ')) return
    event.preventDefault()
    collection.toggleSort(column.id, { append: true })
  }

  return (
    <th
      scope="col"
      aria-sort={ariaSort}
      style={column.width ? { width: column.width } : undefined}
      className={cn('h-11 px-4 text-xs font-semibold uppercase tracking-eyebrow text-ink-muted', align(column), priorityClass(column))}
    >
      {sortable ? (
        <button type="button" onClick={toggle} onKeyDown={handleHeaderKey} className={cn('inline-flex items-center gap-1 rounded focus-visible:outline-none focus-visible:shadow-focus', column.align === 'end' && 'flex-row-reverse')}>
          {column.header}
          {stack.length > 1 && rankIndex >= 0 && <span className="inline-flex min-h-4 min-w-4 items-center justify-center rounded-full bg-whisper px-1 text-micro font-semibold text-ink-muted">{rankIndex + 1}</span>}
          {sorted === 'asc' ? <IconChevronUp size={14} /> : sorted === 'desc' ? <IconChevronDown size={14} /> : <IconChevronExpand size={14} className="opacity-40" />}
        </button>
      ) : column.header}
    </th>
  )
}

function Row<RowT>({ row, rowIndex, collection, selectable, rowActions, onRowClick, rowHeight, expanded, onToggleExpanded, hiddenColumns, getRowSelectLabel, windowed }: {
  row: RowT
  rowIndex: number
  collection: UseCollectionResult<RowT>
  selectable: boolean
  rowActions: RowAction<RowT>[]
  onRowClick?: (row: RowT) => void
  rowHeight: number
  expanded: boolean
  onToggleExpanded: () => void
  hiddenColumns: CollectionColumnDef<RowT>[]
  getRowSelectLabel?: (row: RowT, rowIndex: number) => string
  windowed: boolean
}) {
  return (
    <>
      <tr
        tabIndex={0}
        data-row-index={rowIndex}
        aria-rowindex={windowed ? rowIndex + 1 : undefined}
        onClick={() => onRowClick?.(row)}
        onKeyDown={(event) => handleRowKey(event, row, rowIndex, collection, selectable, onRowClick)}
        style={{ height: rowHeight }}
        className={cn('border-b border-line transition-colors duration-fast ease-out-soft hover:bg-whisper focus-visible:outline-none focus-visible:shadow-focus', onRowClick && 'cursor-pointer')}
      >
        {selectable && (
          <td className="px-3 align-middle" onClick={(event) => event.stopPropagation()}>
            <input type="checkbox" aria-label={getRowSelectLabel?.(row, rowIndex) ?? `Select row ${rowIndex + 1}`} checked={collection.selection.isSelected(row, rowIndex)} onChange={() => collection.selection.toggleRow(row, rowIndex)} className="size-4 rounded border-line focus-visible:outline-none focus-visible:shadow-focus" />
          </td>
        )}
        {hiddenColumns.length > 0 && (
          <td className="px-3 align-middle md:hidden">
            <button type="button" aria-label="Expand row details" aria-expanded={expanded} onClick={(event) => { event.stopPropagation(); onToggleExpanded() }} className="inline-flex min-h-tap min-w-tap items-center justify-center rounded-control text-ink-muted hover:bg-whisper focus-visible:outline-none focus-visible:shadow-focus">
              <IconChevronRight size={16} className={cn('transition-transform duration-fast ease-out-soft', expanded && 'rotate-90')} />
            </button>
          </td>
        )}
        {collection.columns.map((column) => (
          <td key={column.id} className={cn('h-11 min-w-0 max-w-xs px-4 align-middle text-ink-body break-words', align(column), column.numeric && 'tabular-nums', priorityClass(column))}>
            {column.cell ? column.cell(row) : readCell(row, column.id)}
          </td>
        ))}
        {rowActions.length > 0 && <td className="px-3 text-right align-middle"><RowActions row={row} actions={rowActions} /></td>}
      </tr>
      {expanded && hiddenColumns.length > 0 && (
        <tr className="border-b border-line bg-whisper md:hidden">
          <td colSpan={span(collection, selectable, hiddenColumns, rowActions)} className="px-4 py-3">
            <dl className="grid gap-2 text-sm">
              {hiddenColumns.map((column) => <div key={column.id} className="grid grid-cols-[8rem_1fr] gap-3"><dt className="text-ink-muted">{column.header}</dt><dd className="m-0 text-ink-body">{column.cell ? column.cell(row) : readCell(row, column.id)}</dd></div>)}
            </dl>
          </td>
        </tr>
      )}
    </>
  )
}

function handleRowKey<RowT>(event: KeyboardEvent<HTMLTableRowElement>, row: RowT, index: number, collection: UseCollectionResult<RowT>, selectable: boolean, onRowClick?: (row: RowT) => void) {
  if (event.key === 'Enter' && onRowClick) { event.preventDefault(); onRowClick(row) }
  if (event.key === ' ' && selectable) { event.preventDefault(); collection.selection.toggleRow(row, index) }
  if (event.key === 'Home' || event.key === 'End') {
    event.preventDefault()
    const rows = event.currentTarget.closest('tbody')?.querySelectorAll<HTMLElement>('tr[data-row-index]')
    const target = event.key === 'Home' ? rows?.[0] : rows?.[rows.length - 1]
    target?.focus()
    return
  }
  if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return
  event.preventDefault()
  const next = index + (event.key === 'ArrowDown' ? 1 : -1)
  event.currentTarget.closest('tbody')?.querySelector<HTMLElement>(`tr[data-row-index="${next}"]`)?.focus()
}

function getSortStatus<RowT>(collection: UseCollectionResult<RowT>): string {
  const stack = collection.state.sortStack?.length ? collection.state.sortStack : (collection.state.sort ? [collection.state.sort] : [])
  if (!stack.length) return 'Not sorted'
  return stack.map((sort, index) => {
    const column = collection.columns.find((item) => item.id === sort.id)
    const header = typeof column?.header === 'string' ? column.header : sort.id
    return `Sorted by ${header}, ${sort.dir === 'asc' ? 'ascending' : 'descending'}, sort ${index + 1} of ${stack.length}`
  }).join('. ')
}

function readCell<RowT>(row: RowT, id: string): ReactNode {
  if (typeof row === 'object' && row !== null && !Array.isArray(row) && id in row) return (row as Record<string, ReactNode>)[id]
  return null
}

function align<RowT>(column: CollectionColumnDef<RowT>): string {
  return column.align === 'end' || column.numeric ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left'
}

function priorityClass<RowT>(column: CollectionColumnDef<RowT>): string {
  return (column.priority ?? 1) > 1 ? 'hidden md:table-cell' : ''
}

function span<RowT>(collection: UseCollectionResult<RowT>, selectable: boolean, hidden: CollectionColumnDef<RowT>[], actions: RowAction<RowT>[]): number {
  return collection.columns.length + (selectable ? 1 : 0) + (hidden.length > 0 ? 1 : 0) + (actions.length > 0 ? 1 : 0)
}

function toggleSet(current: Set<string>, key: string): Set<string> {
  const next = new Set(current)
  if (next.has(key)) {
    next.delete(key)
  } else {
    next.add(key)
  }
  return next
}
